import KnowledgeBasesAPI from 'dashboard/api/pipelines/knowledgeBases';
import KnowledgeSourcesAPI from 'dashboard/api/pipelines/knowledgeSources';

const state = {
  records: {},
  sources: {},
  chunks: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getKnowledgeBases(_state) {
    return Object.values(_state.records).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  },
  getKnowledgeBase: _state => id => {
    return _state.records[id] || null;
  },
  getSourcesByKnowledgeBase: _state => knowledgeBaseId => {
    return (_state.sources[knowledgeBaseId] || []).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  },
  getChunksBySource: _state => sourceId => {
    return _state.chunks[sourceId] || [];
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }, { accountId }) => {
    commit('SET_KB_UI_FLAG', { isFetching: true });
    try {
      const response = await KnowledgeBasesAPI.get(accountId);
      commit('SET_KNOWLEDGE_BASES', response.data.data || []);
    } finally {
      commit('SET_KB_UI_FLAG', { isFetching: false });
    }
  },

  create: async ({ commit }, { accountId, data }) => {
    commit('SET_KB_UI_FLAG', { isCreating: true });
    try {
      const response = await KnowledgeBasesAPI.create(accountId, data);
      commit('ADD_KNOWLEDGE_BASE', response.data.data);
      return response.data.data;
    } finally {
      commit('SET_KB_UI_FLAG', { isCreating: false });
    }
  },

  update: async ({ commit }, { accountId, id, data }) => {
    commit('SET_KB_UI_FLAG', { isUpdating: true });
    try {
      const response = await KnowledgeBasesAPI.update(accountId, id, data);
      commit('UPDATE_KNOWLEDGE_BASE', response.data.data);
      return response.data.data;
    } finally {
      commit('SET_KB_UI_FLAG', { isUpdating: false });
    }
  },

  delete: async ({ commit }, { accountId, id }) => {
    commit('SET_KB_UI_FLAG', { isDeleting: true });
    try {
      await KnowledgeBasesAPI.delete(accountId, id);
      commit('DELETE_KNOWLEDGE_BASE', id);
    } finally {
      commit('SET_KB_UI_FLAG', { isDeleting: false });
    }
  },

  // Sources
  getSources: async ({ commit }, { accountId, knowledgeBaseId }) => {
    const response = await KnowledgeSourcesAPI.getByKnowledgeBase(accountId, knowledgeBaseId);
    commit('SET_SOURCES', { knowledgeBaseId, sources: response.data.data || [] });
  },

  createSource: async ({ commit }, { accountId, knowledgeBaseId, data }) => {
    const response = await KnowledgeSourcesAPI.createForKnowledgeBase(accountId, knowledgeBaseId, data);
    commit('ADD_SOURCE', { knowledgeBaseId, source: response.data.data });
    return response.data.data;
  },

  updateSource: async ({ commit }, { accountId, knowledgeBaseId, sourceId, data }) => {
    const response = await KnowledgeSourcesAPI.updateSource(accountId, knowledgeBaseId, sourceId, data);
    commit('UPDATE_SOURCE', { knowledgeBaseId, source: response.data.data });
    return response.data.data;
  },

  deleteSource: async ({ commit }, { accountId, knowledgeBaseId, sourceId }) => {
    await KnowledgeSourcesAPI.deleteSource(accountId, knowledgeBaseId, sourceId);
    commit('DELETE_SOURCE', { knowledgeBaseId, sourceId });
  },

  getChunks: async ({ commit }, { accountId, knowledgeBaseId, sourceId }) => {
    const response = await KnowledgeSourcesAPI.getChunks(accountId, knowledgeBaseId, sourceId);
    commit('SET_CHUNKS', { sourceId, chunks: response.data || [] });
    return response.data;
  },

  updateChunk: async ({ commit }, { accountId, knowledgeBaseId, sourceId, chunkId, content }) => {
    const response = await KnowledgeSourcesAPI.updateChunk(accountId, knowledgeBaseId, sourceId, chunkId, content);
    commit('UPDATE_CHUNK', { sourceId, chunk: response.data });
    return response.data;
  },

  deleteChunk: async ({ commit }, { accountId, knowledgeBaseId, sourceId, chunkId }) => {
    await KnowledgeSourcesAPI.deleteChunk(accountId, knowledgeBaseId, sourceId, chunkId);
    commit('DELETE_CHUNK', { sourceId, chunkId });
  },

  reprocessSource: async ({ commit }, { accountId, knowledgeBaseId, sourceId }) => {
    const response = await KnowledgeSourcesAPI.reprocess(accountId, knowledgeBaseId, sourceId);
    commit('UPDATE_SOURCE', { knowledgeBaseId, source: response.data.data });
    return response.data.data;
  },
};

export const mutations = {
  SET_KB_UI_FLAG(_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  SET_KNOWLEDGE_BASES(_state, records) {
    const map = {};
    records.forEach(kb => { map[kb.id] = kb; });
    _state.records = map;
  },
  ADD_KNOWLEDGE_BASE(_state, kb) {
    _state.records = { ..._state.records, [kb.id]: kb };
  },
  UPDATE_KNOWLEDGE_BASE(_state, kb) {
    _state.records = { ..._state.records, [kb.id]: kb };
  },
  DELETE_KNOWLEDGE_BASE(_state, id) {
    const records = { ..._state.records };
    delete records[id];
    _state.records = records;
  },
  SET_SOURCES(_state, { knowledgeBaseId, sources }) {
    _state.sources = { ..._state.sources, [knowledgeBaseId]: sources };
  },
  ADD_SOURCE(_state, { knowledgeBaseId, source }) {
    const current = _state.sources[knowledgeBaseId] || [];
    _state.sources = { ..._state.sources, [knowledgeBaseId]: [...current, source] };
  },
  UPDATE_SOURCE(_state, { knowledgeBaseId, source }) {
    const current = _state.sources[knowledgeBaseId] || [];
    _state.sources = {
      ..._state.sources,
      [knowledgeBaseId]: current.map(s => (s.id === source.id ? source : s)),
    };
  },
  UPDATE_SOURCE_STATUS(_state, { knowledge_base_id, id, status, error_message }) {
    const sources = _state.sources[knowledge_base_id] || [];
    _state.sources = {
      ..._state.sources,
      [knowledge_base_id]: sources.map(s =>
        s.id === id ? { ...s, status, error_message } : s
      ),
    };
  },
  SET_CHUNKS(_state, { sourceId, chunks }) {
    _state.chunks = { ..._state.chunks, [sourceId]: chunks };
  },
  UPDATE_CHUNK(_state, { sourceId, chunk }) {
    const current = _state.chunks[sourceId] || [];
    _state.chunks = { ..._state.chunks, [sourceId]: current.map(c => c.id === chunk.id ? chunk : c) };
  },
  DELETE_CHUNK(_state, { sourceId, chunkId }) {
    const current = _state.chunks[sourceId] || [];
    _state.chunks = { ..._state.chunks, [sourceId]: current.filter(c => c.id !== chunkId) };
  },
  DELETE_SOURCE(_state, { knowledgeBaseId, sourceId }) {
    const current = _state.sources[knowledgeBaseId] || [];
    _state.sources = {
      ..._state.sources,
      [knowledgeBaseId]: current.filter(s => s.id !== sourceId),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
